import React, { useState, useEffect } from 'react';
import { getDatabase, ref as dbRef, onValue } from 'firebase/database';
import "./PreviousOrders.css";
import { FaInfoCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useCurrentUid } from "../hooks/useCurrentUid";     // ✅ NEW
import { MdInfo } from 'react-icons/md';
import { getDownloadURL, storage, storageRef } from '../Firebase/firebase-config';

const PreviousOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('All');

  const navigate = useNavigate();
  const currentUid = useCurrentUid();                       // ✅ USING AUTHCONTEXT UID
  const database = getDatabase();

  useEffect(() => {
    if (!navigator.onLine) {
      setError("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    if (!currentUid) {
      setError("User is not authenticated. Please login again.");
      setLoading(false);
      return;
    }

    const connectedRef = dbRef(database, ".info/connected");
    const ordersRef = dbRef(database, `orders/${currentUid}`);

    // Listener: Check Firebase connection
    const unsubscribeConnected = onValue(connectedRef, (snap) => {
      if (snap.val() === false) {
        setError("Session expired. Please login again.");
        setLoading(false);
        return;
      }

      setError("");

      // Listener: Fetch user orders
      const unsubscribeOrders = onValue(
        ordersRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const orderList = Object.keys(data)
              .map((key) => ({ id: key, ...data[key] }))
              .reverse();

            setOrders(orderList);
            setFilteredOrders(orderList);
          } else {
            setOrders([]);
            setFilteredOrders([]);
          }
          setLoading(false);
        },
        (err) => {
          console.error("Firebase fetch error:", err);
          setError("Error fetching orders. Please try again later.");
          setLoading(false);
        }
      );

      // Cleanup order listener
      return () => unsubscribeOrders();
    });

    return () => unsubscribeConnected();
  }, [currentUid]);     // 👈 AUTHCONTEXT UID DEPENDENCY

  const orderInfo = () => {
    alert(
      "View your past orders including details like the brand, grade, site address, quantity, and payment information. You can also access the proof of payment and purchase order files (if available)."
    );
  };

  const handleFilterChange = (e) => {
    const selected = e.target.value;
    setFilter(selected);

    const now = new Date();

    const filtered = orders.filter((order) => {
      const orderDate = new Date(order.timestamp);
      switch (selected) {
        case "Today":
          return orderDate.toDateString() === now.toDateString();

        case "This Week": {
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          return orderDate >= startOfWeek && orderDate <= endOfWeek;
        }

        case "This Month":
          return (
            orderDate.getMonth() === now.getMonth() &&
            orderDate.getFullYear() === now.getFullYear()
          );

        case "Previous":
          return orderDate < now;

        default:
          return true;
      }
    });

    setFilteredOrders(selected === "All" ? orders : filtered);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const openFile = async (fileValue, fileType) => {
    if (!fileValue) {
      alert(`No ${fileType} attached.`)
      return
    }

    let url = fileValue

    // If it's NOT a full URL (i.e., it's a storage path), generate fresh URL
    if (!fileValue.startsWith("http")) {
      try {
        const fileRef = storageRef(storage, fileValue)
        url = await getDownloadURL(fileRef)
      } catch (error) {
        console.error("Error generating URL from path:", error)
        alert(`Unable to open ${fileType}. The file may have been deleted.`)
        return
      }
    }

    // Now open the (valid) URL
    window.open(url, "_blank", "noopener,noreferrer")
  }

 const formatDateTime = (timestamp) => {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

  return (
    <div className="order-page-container">
      <div className="order-header">
        <h2>
          Order History{" "}
          <MdInfo
            className='icon-i'
            style={{ cursor: "pointer" }}
            onClick={orderInfo}
          />
        </h2>

        <select
          className="filter-dropdown"
          value={filter}
          onChange={handleFilterChange}
        >
          <option value="All">All</option>
          <option value="Today">Today</option>
          <option value="This Week">This Week</option>
          <option value="This Month">This Month</option>
          <option value="Previous">Previous</option>
        </select>
      </div>

      <div className="order-list">
        {filteredOrders.length === 0 ? (
          <div className="no-orders">No orders found for selected filter.</div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-info">
                <p><strong>Brand:</strong> {order.brand}</p>
                <p><strong>Grade:</strong> {order.grade}</p>
                <p>
                  <strong>Site Address:</strong>{" "}
                  {order.siteAddress?.address}, {order.siteAddress?.state},{" "}
                  {order.siteAddress?.pincode}
                </p>
                <p>
                  <strong>Quantity:</strong> {order.quantity}{" "}
                  {order.quantityType}
                </p>
                <p>
                  <strong>Payment Amount:</strong> ₹{order.paymentAmount}
                </p>

                {/* Smart PO File View - Handles both old URLs and new paths */}
                {order.poFileUrl && (
                  <p>
                    <strong>P.O File:</strong>{" "}
                    <button
                      type="button"
                      onClick={() => openFile(order.poFileUrl, "Purchase Order")}
                      className="file-view-btn"
                    >
                      View File
                    </button>
                  </p>
                )}

                {/* Smart Payment Proof View */}
                {order.paymentFileUrl && (
                  <p>
                    <strong>Payment Proof:</strong>{" "}
                    <button
                      type="button"
                      onClick={() => openFile(order.paymentFileUrl, "Payment Proof")}
                      className="file-view-btn"
                    >
                      View File
                    </button>
                  </p>
                )}

                <p><strong>Timestamp:</strong>  {formatDateTime(order.timestamp)}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PreviousOrders;
